import { openAnswersProperty, hasOpenAnswers } from "../OpenAnswers";
import { SearchResultData } from "../../app/slices/SearchResultsSlice";
import { LocalizedStringRecord } from "../../app/slices/TrainingLocalizationSlice";
import { InteractionFactoryEntity } from "../../features/interaction_factory/InteractionFactory";
import StatementQuizPreview from "../../features/previews/statement_quiz/StatementQuizPreview";
import { GetMigratedProperties } from "../../features/project_migration/InteractionMigrationHelper";
import { ValidationHelper } from "../../features/validation/ValidationHelper";
import { GetNewGuid } from "../../helpers/GuidHelper";
import { Actor } from "../Actor";
import { requiredGlobalQuizProperties } from "../GlobalQuizProperty";
import { InteractionProperty } from "../InteractionProperty";
import { Prop } from "../Prop";
import { IAnswer } from "../Quiz";
import Take from "../Take";
import Interaction, { InteractionType } from "./Interaction";

export const requiredStatementQuizProperties: InteractionProperty[] = [
  ...requiredGlobalQuizProperties,
  openAnswersProperty,
];

export interface IStatementQuiz extends Interaction {
  title: string;
  answers: string[];
  statements: string[];
  correctAnswers: number[];
}

export const statementQuizInteraction = (): InteractionFactoryEntity => {
  const component = <StatementQuizPreview />;

  const create = (getKeys: (amount: number) => string[]): IStatementQuiz => {
    const keys = getKeys(4);

    return {
      id: GetNewGuid(),
      name: "Statement Quiz",
      type: InteractionType.StatementQuiz,
      title: keys[0],
      answers: [keys[1], keys[2]],
      statements: [keys[3]],
      correctAnswers: [-1],
      properties: [...requiredStatementQuizProperties],
    };
  };

  const migrate = (
    from: number,
    to: number,
    interaction: Interaction
  ): IStatementQuiz => {
    const casted = interaction as IStatementQuiz;

    if (casted.properties === undefined) {
      console.log(
        "[Statement Quiz] migrated " +
          requiredStatementQuizProperties.length +
          " properties"
      );
      return { ...casted, properties: requiredStatementQuizProperties };
    }

    var newProperties: InteractionProperty[] = GetMigratedProperties(
      requiredStatementQuizProperties,
      casted.properties
    );

    console.log(
      "[Statement Quiz] migrated " +
        (newProperties.length - casted.properties.length) +
        " properties"
    );

    return { ...casted, properties: newProperties };
  };

  const copy = (
    interaction: Interaction,
    getKeys: (amount: number) => string[],
    replaceKey: (from: string, to: string) => void,
    getAnswerArray: (originalAnswers: IAnswer[], newKeys: string[]) => IAnswer[]
  ): IStatementQuiz => {
    const original = interaction as IStatementQuiz;
    const keys = getKeys(
      1 + original.answers.length + original.statements.length
    );
    const title = keys[0];
    const answers = keys.slice(1, 1 + original.answers.length);
    const statements = keys.slice(1 + original.answers.length);

    replaceKey(original.title, title);
    original.answers.forEach((answer, index) => {
      replaceKey(answer, answers[index]);
    });
    original.statements.forEach((statement, index) => {
      replaceKey(statement, statements[index]);
    });

    return {
      ...original,
      id: GetNewGuid(),
      title,
      answers,
      statements,
      correctAnswers: [...(original.correctAnswers ?? [])],
      properties: original.properties?.map((property) => ({ ...property })),
    };
  };

  const validate = (
    interaction: Interaction,
    getValue: (key: string) => string,
    take: Take,
    actorPresets: Actor[]
  ): string[] => {
    const converted = interaction as IStatementQuiz;
    const warnings: string[] = [];

    // validate title
    ValidationHelper.ValidateKey(converted.title, getValue, warnings, "Title");
    converted.answers.forEach((answer, index) => {
      ValidationHelper.ValidateKey(answer, getValue, warnings, `Answer ${index + 1}`);
    });

    // check if statements are not empty
    converted.statements.forEach((statement, index) => {
      ValidationHelper.ValidateKey(
        statement,
        getValue,
        warnings,
        "Statement " + (index + 1)
      );
    });

    return warnings;
  };

  const filter = (
    interaction: Interaction,
    takeName: string,
    getMatchingResults: (
      searchableStrings: string[],
      takeName: string,
      interaction: Interaction,
      filterType: string
    ) => SearchResultData[]
  ): SearchResultData[] => {
    const converted = interaction as IStatementQuiz;
    const searchableStrings: string[] = [
      interaction.name,
      converted.title,
      ...converted.statements,
      ...converted.answers,
    ];

    return getMatchingResults(
      searchableStrings,
      takeName,
      interaction,
      filterType
    );
  };

  const filterType: string = "Statement Quiz";

  const validateErrors = (
    interaction: Interaction,
    parentTake: Take,
    currentTake: Take,
    actorPresets: Actor[],
    allPropPresets: Prop[],
    localizedStringRecords: LocalizedStringRecord
  ): string[] => {
    const casted = interaction as IStatementQuiz;
    const errors: string[] = [];

    if (casted.statements.length === 0) errors.push("Please add a statement");
    if (casted.answers.length === 0) errors.push("Please add an answer");
    for (let i = 0; i < casted.statements.length; i++) {
      const correctIndex = casted.correctAnswers?.[i];
      if (
        !hasOpenAnswers(casted) &&
        (!Number.isInteger(correctIndex) || correctIndex < 0 || correctIndex >= casted.answers.length)
      ) {
        errors.push("Correct answer not selected for statement " + (i + 1));
      }
    }

    return errors;
  };

  return {
    View: component,
    FilterType: filterType,
    Create: create,
    Migrate: migrate,
    Copy: copy,
    Validate: validate,
    Filter: filter,
    ValidateErrors: validateErrors,
  };
};
